import { merge } from '../../helper';

import isEmptyObject from '../../general/isEmpty/isEmptyObject';
import setAttr from '../../general/attr/setAttr';

import type { IOptionConfig } from '../option';
import * as exOption from '../option';
import type { IElementConfig } from '../../utils/element/types';
import * as exElement from '../../utils/element';
import * as exEvent from '../../utils/event';

import * as exSelect from './';

// ----------------------------------------------------------------------------

export interface ISelectConfig extends IElementConfig {
    multiple?: boolean;
    required?: boolean;
    options?: IOptionConfig;
    value?: string | string[];
    autoSelected?: boolean;
    event?: Record<string, any>;
}

export type TypeBuildReturn = {
    element: HTMLSelectElement;
    elements: {
        element: HTMLSelectElement;
        options?: HTMLOptionElement[];
    };
};

// ----------------------------------------------------------------------------

function create(config: ISelectConfig = {}): TypeBuildReturn {
    // Default Config
    const defConfig: ISelectConfig = {
        autoSelected: true,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    //
    const build = {
        elements: {},
    } as TypeBuildReturn;

    // ------------------------------------------------------------------------

    // Create element (select)
    const elSelect = exElement.create({
        merge: {
            ...defConfig,
            event: null,
        },
        tag: 'select',
        class: {
            new: 'form-select',
        },
    }) as HTMLSelectElement;
    build.elements.element = elSelect;
    build.element = elSelect;

    // ------------------------------------------------------------------------

    // Settings
    settings(build, defConfig);

    // Events
    events(build, defConfig);

    // ------------------------------------------------------------------------

    // Return
    return build;
}

function settings(build: TypeBuildReturn, config: ISelectConfig = {}): void {
    // If using multiple
    if (config.multiple === true) {
        setAttr(build.element, 'multiple', '');
    }

    // If using required
    if (config.required === true) {
        setAttr(build.element, 'required', '');
    }

    // ------------------------------------------------------------------------

    // If using options
    if (!isEmptyObject(config.options)) {
        // Create element (option)
        const options = exOption.create(config.options);
        build.element.appendChildren(options);
        build.elements.options = options;
    }

    // ------------------------------------------------------------------------

    // If using value
    if (config.value != null) {
        exSelect.setValue(build.element, config.value);
    }
}

function events(build: TypeBuildReturn, config: ISelectConfig = {}): void {
    // If using auto selected
    if (config.autoSelected === true) {
        // When selecting an option, check whether it is selected or not

        // Create event (change)
        build.element.addEventListener('change', function (this: any): any {
            // Foreach Options
            for (const elOption of this.options) {
                //
                if (elOption.selected) {
                    elOption.setAttribute('selected', '');
                }
                //
                else {
                    elOption.removeAttribute('selected');
                }
            }
        });
    }

    // ------------------------------------------------------------------------

    // If using event
    if (config.event != null) {
        exEvent.create(build.element, config.event);
    }
}

// ----------------------------------------------------------------------------

export { create };
